import { Stack, Tooltip } from "@mui/material";
import { AutocompleteRenderGetTagProps } from "@mui/material/Autocomplete/Autocomplete";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { MdsIcon } from "@sinch/components";
import { NotificationChannel } from "@sinch/components/application/Messages/useMessageChannels";
import { useRoleStatus } from "@sinch/components/application/Worker/useRoleStatus";
import { AutocompleteOptions } from "@sinch/components/form/AutocompleteInput/types";
import { RoleStatusEnum } from "@sinch/core/entities/serverEnums";
import { MdsCloseXs } from "@sinch/icons";
import { isTouchDevice } from "@sinch/utils/browser";
import { isNil } from "ramda";
import { isNotNil } from "ramda-adjunct";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { RecipientFragment } from "@sinch/components/application/Messages/MessageForm/queries.types";

export interface RecipientsOptions extends AutocompleteOptions {
  availableChannels?: string[];
  roleStatus?: RoleStatusEnum;
}

interface RecipientTagProps {
  recipientData: RecipientFragment[];
  value: string | number;
  label: string;
  channels: NotificationChannel[] | null;
  /** Whether this tag represents a user group rather than a single recipient. */
  isGroup?: boolean;
  /** Optional extra content shown after the label. */
  hint?: ReactNode;
  tagProps: Omit<ReturnType<AutocompleteRenderGetTagProps>, "key">;
}

export const RecipientTag = ({ recipientData, value, label, channels, isGroup, hint, tagProps }: RecipientTagProps) => {
  const { t } = useTranslation();
  const { getRoleStatusColor, getRoleStatusLabel, getRoleStatusIcon } = useRoleStatus();
  const isTouch = isTouchDevice();

  const matchedOption = recipientData?.find((item) => value === item.id);
  const { availableNotificationChannels, roleStatus } = matchedOption ?? {
    availableChannels: null,
    roleStatus: null,
  };

  // A non-group id with no matching option (e.g. a deleted/out-of-scope recipient
  // never returned by the query) would otherwise render its raw id as the label.
  // Show a clearly-invalid "Unknown recipient" chip instead; the user removes it
  // via the chip's delete icon.
  const isUnresolved = !isGroup && isNil(matchedOption);
  const displayLabel = isUnresolved ? t("MessageDialog.unknownRecipient") : label;

  const isValid =
    !isUnresolved &&
    (isNil(availableNotificationChannels) ||
      (channels?.some((channel) => availableNotificationChannels?.includes(channel)) ?? false));

  return (
    <Chip
      {...tagProps}
      color={!isValid ? "error" : isGroup ? "info" : "default"}
      deleteIcon={<MdsIcon fontSize="xs" icon={MdsCloseXs} />}
      label={
        <Stack alignItems="center" direction="row" gap={1}>
          {displayLabel}
          {isNotNil(roleStatus) &&
            roleStatus !== RoleStatusEnum.Active &&
            (isTouch ? (
              <Typography sx={{ color: getRoleStatusColor(roleStatus) }}>{getRoleStatusLabel(roleStatus)}</Typography>
            ) : (
              <Tooltip title={getRoleStatusLabel(roleStatus)}>
                <MdsIcon
                  fontSize="xs"
                  icon={getRoleStatusIcon(roleStatus)}
                  sx={{ color: getRoleStatusColor(roleStatus) }}
                />
              </Tooltip>
            ))}
          {hint}
        </Stack>
      }
      variant={!isValid ? "outlined" : "filled"}
    />
  );
};
